/**
 * Minimal fetch-based Server-Sent Events client.
 *
 * The native `EventSource` API can't attach an Authorization header, and this
 * app's auth is Bearer-token-in-header (see the axios interceptor in api.ts)
 * with a short-lived access token backed by a refresh token — a plain
 * `new EventSource(url)` could neither authenticate the request nor survive a
 * token refresh mid-stream. This reads the response body as a stream instead,
 * reusing the same token + refresh-token flow as every other request.
 */
import { API_BASE_URL } from './api'
import { getAccessToken, getRefreshToken, setAccessToken, setTokenPair } from './tokenStorage'

/**
 * Treat a stream that has sent nothing at all for this long as dead. The server
 * emits a keep-alive comment roughly every 15s (see stream_campaign_events), so
 * three missed keep-alives is unambiguous rather than merely quiet.
 */
const STALL_MS = 45_000

/** Give up on the stream and fall back to polling after this many consecutive
 * failed connection attempts — a stall that recurs is not transient. */
const MAX_ATTEMPTS_BEFORE_FALLBACK = 4

interface EventStreamOptions {
  onMessage: (data: any) => void
  /** Called when the stream gives up entirely (not on every transient retry) — the caller should fall back to polling. */
  onError?: (err: unknown) => void
  signal?: AbortSignal
  /**
   * Set for streams the server never ends on purpose (e.g. the notification
   * bell). A clean close then means a proxy timeout or server restart, so
   * it's retried like any transient failure instead of silently stopping.
   * Leave unset for streams with a terminal state (campaign events close
   * after the final snapshot).
   */
  reconnectOnClose?: boolean
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${refreshToken}` },
    })
    if (!res.ok) return null
    const data = await res.json()
    if (!data.access_token) return null
    // The endpoint rotates the refresh token; persist it or this long-lived
    // stream keeps replaying a superseded one for the rest of the session.
    // `refresh_token` is optional so an older backend still works.
    if (data.refresh_token) {
      setTokenPair(data.access_token, data.refresh_token)
    } else {
      setAccessToken(data.access_token)
    }
    return data.access_token as string
  } catch {
    return null
  }
}

/**
 * Opens one SSE connection to `path` (relative to the API base), calling
 * `onMessage` with the parsed JSON payload of each event. Reconnects with
 * exponential backoff on a dropped connection or transient (5xx/network)
 * error; on a 401 it refreshes the access token once (mirroring the axios
 * interceptor) and retries immediately. A 404/503 (campaign gone / Redis
 * unavailable — see stream_campaign_events) is treated as permanent: it
 * calls `onError` once and stops, so the caller can fall back to polling
 * instead of retrying an endpoint that will never succeed this session.
 *
 * Returns a function that closes the stream and stops all retries.
 */
export function openEventStream(
  path: string,
  { onMessage, onError, signal, reconnectOnClose }: EventStreamOptions,
): () => void {
  const controller = new AbortController()
  if (signal) {
    if (signal.aborted) controller.abort()
    else signal.addEventListener('abort', () => controller.abort())
  }
  let stopped = false
  let attempt = 0

  const run = async () => {
    while (!stopped) {
      const token = getAccessToken()
      try {
        const res = await fetch(`${API_BASE_URL}${path}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        })

        if (res.status === 401) {
          const refreshed = await refreshAccessToken()
          if (!refreshed) {
            onError?.(new Error('Unauthorized'))
            return
          }
          continue // retry immediately with the refreshed token, no backoff
        }

        if (res.status === 404 || res.status === 503) {
          // Campaign gone, or Redis unavailable — won't resolve itself this
          // session; stop instead of hammering an endpoint that can't succeed.
          onError?.(new Error(`Stream unavailable: ${res.status}`))
          return
        }

        if (!res.ok || !res.body) {
          throw new Error(`Stream request failed: ${res.status}`)
        }

        attempt = 0 // connected — reset backoff
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          // Stall guard. A 200 with a body that never yields is indistinguishable
          // from a healthy idle stream to `read()`, which just waits forever — so
          // a buffering proxy, a silently dropped connection, or a half-open
          // socket would freeze the page with no error and no fallback. The
          // server sends a keep-alive comment about every 15s, so silence past
          // STALL_MS means the stream is dead regardless of what the socket
          // claims: abandon it and let the retry loop reconnect (or, after
          // repeated failures, hand over to polling).
          const { value, done } = await Promise.race([
            reader.read(),
            new Promise<never>((_resolve, reject) =>
              setTimeout(() => reject(new Error('Stream stalled')), STALL_MS),
            ),
          ])
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          let sepIndex
          while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, sepIndex)
            buffer = buffer.slice(sepIndex + 2)
            const dataLines = rawEvent
              .split('\n')
              .filter((line) => line.startsWith('data:'))
              .map((line) => line.slice(5).trimStart())
            if (dataLines.length === 0) continue // e.g. a ": keep-alive" comment line
            try {
              onMessage(JSON.parse(dataLines.join('\n')))
            } catch {
              // Malformed payload — skip rather than crash the stream.
            }
          }
        }
        // Server closed the stream cleanly (it sends one final snapshot then
        // returns once the campaign leaves the live states) — don't
        // reconnect; the caller re-opens if the campaign goes live again.
        // Endless streams opt into treating this as transient instead (see
        // reconnectOnClose above); the throw lands in the retry path below.
        if (reconnectOnClose) throw new Error('Stream closed by server')
        return
      } catch (err) {
        if (controller.signal.aborted) return
        // Transient (network blip, connection reset, 5xx, a stall caught by the
        // guard above) — retry silently with backoff rather than surfacing it,
        // same as the polling this replaces (which failed silently via
        // `.catch(() => {})`).
        attempt += 1
        if (attempt >= MAX_ATTEMPTS_BEFORE_FALLBACK) {
          // Repeated failures are not a blip: something about this session
          // (proxy, network, environment) can't hold a stream. Hand over to
          // polling instead of retrying forever behind a page that looks live
          // but never updates.
          onError?.(err ?? new Error('Stream unavailable'))
          return
        }
        const delay = Math.min(1000 * 2 ** attempt, 15000)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }

  run()

  return () => {
    stopped = true
    controller.abort()
  }
}
